﻿import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { adminStrings } from './admin-strings';
import { createAdminSession, readAdminError } from './admin-session';
import { pickTeaserPhoto } from './insights-admin-format';
import type { InsightsReport } from './insights-admin-format';
import { createIdentityId, getOrCreateStoredBrowserId } from './telemetry-identity';
import './admin.css';

type TelemetryRange = 'today' | '7days' | '30days';

type RankedValue = {
    value: string;
    count: number;
};

type ActiveSession = {
    sessionId: string;
    browserId: string;
    startedUtc: string;
    landingPath: string;
    referrer: string | null;
    currentPath: string;
};

type DurationBucket = {
    startSeconds: number;
    endSeconds: number | null;
    count: number;
};

type TelemetrySnapshot = {
    range: TelemetryRange;
    generatedUtc: string;
    connectedBrowsers: number;
    pageViews: number;
    uniqueVisitors: number;
    averageHomepageSessionMinutes: number | null;
    homepageSessionDurations: DurationBucket[];
    topPages: RankedValue[];
    topReferrers: RankedValue[];
    activeSessions: ActiveSession[];
};

type ExcludedBrowser = {
    browserId: string;
    label: string | null;
    excludedUtc: string;
};

const statusSurface = requiredElement<HTMLElement>('status');
const connectedBrowsers = requiredElement<HTMLElement>('connected-browsers');
const pageViews = requiredElement<HTMLElement>('page-views');
const uniqueVisitors = requiredElement<HTMLElement>('unique-visitors');
const averageHomepageSession = requiredElement<HTMLElement>('average-homepage-session');
const pageViewsPeriod = requiredElement<HTMLElement>('page-views-period');
const uniqueVisitorsPeriod = requiredElement<HTMLElement>('unique-visitors-period');
const topPages = requiredElement<HTMLOListElement>('top-pages');
const topReferrers = requiredElement<HTMLOListElement>('top-referrers');
const homepageSessionDurations = requiredElement<HTMLOListElement>('homepage-session-durations');
const trafficFreshness = requiredElement<HTMLElement>('traffic-freshness');
const activeSessionsBody = requiredElement<HTMLTableSectionElement>('active-sessions-body');
const rangeButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.traffic-range'));
const excludeMyVisitsButton = requiredElement<HTMLButtonElement>('exclude-my-visits');
const excludedBrowsersList = requiredElement<HTMLUListElement>('excluded-browsers');
const excludeVisitsDialog = requiredElement<HTMLDialogElement>('exclude-visits-dialog');
const excludeVisitsLabel = requiredElement<HTMLInputElement>('exclude-visits-label');
const excludeVisitsConfirm = requiredElement<HTMLButtonElement>('exclude-visits-confirm');
const excludeVisitsCancel = requiredElement<HTMLButtonElement>('exclude-visits-cancel');
const excludeVisitsTitle = requiredElement<HTMLElement>('exclude-visits-title');
const numberFormatter = new Intl.NumberFormat('en-US');
const dashboardConnection = new HubConnectionBuilder()
    .withUrl('/hubs/admin-telemetry')
    .withAutomaticReconnect([0, 2_000, 10_000, 30_000])
    .configureLogging(LogLevel.None)
    .build();
let selectedRange: TelemetryRange = 'today';
let authenticated = false;
let connectionRetryTimeoutId: number | null = null;
// Storage may be unavailable; an in-memory id still works for this page.
const browserId = getOrCreateStoredBrowserId() ?? createIdentityId();
let excludedBrowserIds = new Set<string>();
let pendingExcludeBrowserId: string | null = null;

function requiredElement<T extends HTMLElement>(id: string): T {
    const element = document.getElementById(id);
    if (element == null) throw new Error(`Required element #${id} was not found.`);
    return element as T;
}

function setStatus(message: string, kind: 'status' | 'error' = 'status'): void {
    statusSurface.textContent = message;
    statusSurface.dataset.kind = kind;
}

function isTelemetryRange(value: string | undefined): value is TelemetryRange {
    return value === 'today' || value === '7days' || value === '30days';
}

function clearTelemetry(): void {
    connectedBrowsers.textContent = '—';
    pageViews.textContent = '—';
    uniqueVisitors.textContent = '—';
    averageHomepageSession.textContent = '—';
    topPages.replaceChildren();
    topReferrers.replaceChildren();
    homepageSessionDurations.replaceChildren();
    activeSessionsBody.replaceChildren();
    trafficFreshness.textContent = '';
}

function formatElapsedDuration(startedUtc: string, generatedUtc: string): string {
    const elapsedSeconds = Math.max(0, Math.floor((Date.parse(generatedUtc) - Date.parse(startedUtc)) / 1000));
    const hours = Math.floor(elapsedSeconds / 3_600);
    const minutes = Math.floor((elapsedSeconds % 3_600) / 60);
    const seconds = elapsedSeconds % 60;
    return hours > 0 ? `${hours}h ${minutes}m ${seconds}s` : `${minutes}m ${seconds}s`;
}

function renderActiveSessions(sessions: ActiveSession[], generatedUtc: string): void {
    activeSessionsBody.replaceChildren();
    if (sessions.length === 0) {
        const row = document.createElement('tr');
        const cell = document.createElement('td');
        cell.colSpan = 4;
        cell.textContent = adminStrings.telemetryNoActiveSessions;
        row.append(cell);
        activeSessionsBody.append(row);
        return;
    }

    for (const session of sessions) {
        const row = document.createElement('tr');
        const landingPage = document.createElement('td');
        const referrer = document.createElement('td');
        const duration = document.createElement('td');
        const actions = document.createElement('td');
        const excludeButton = document.createElement('button');
        excludeButton.type = 'button';
        excludeButton.className = 'secondary';
        excludeButton.textContent = adminStrings.excludeSessionBrowser;
        excludeButton.addEventListener('click', () => openExcludeDialog(session.browserId));
        actions.append(excludeButton);
        landingPage.textContent = session.landingPath;
        duration.textContent = formatElapsedDuration(session.startedUtc, generatedUtc);
        // Referrers are stored as host and path with no scheme, and come from visitors' browsers: text, never links.
        referrer.textContent = session.referrer ?? adminStrings.telemetryDirectReferrer;
        row.append(landingPage, referrer, duration, actions);
        activeSessionsBody.append(row);
    }
}

function formatDuration(minutes: number | null): string {
    if (minutes == null) return '—';
    const totalSeconds = Math.round(minutes * 60);
    const wholeMinutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${wholeMinutes}m ${seconds}s`;
}

function renderDurationHistogram(buckets: DurationBucket[]): void {
    homepageSessionDurations.replaceChildren();
    const maximumCount = Math.max(0, ...buckets.map(bucket => bucket.count));
    for (const bucket of buckets) {
        const item = document.createElement('li');
        const label = document.createElement('span');
        const bar = document.createElement('span');
        const fill = document.createElement('span');
        const count = document.createElement('strong');
        const range = formatDurationRange(bucket.startSeconds, bucket.endSeconds);
        label.className = 'session-duration-label';
        label.textContent = range;
        bar.className = 'session-duration-bar';
        fill.className = 'session-duration-fill';
        fill.style.width = maximumCount === 0 ? '0' : `${(bucket.count / maximumCount) * 100}%`;
        count.textContent = numberFormatter.format(bucket.count);
        item.setAttribute('aria-label', `${range}: ${count.textContent} sessions`);
        bar.append(fill);
        item.append(label, bar, count);
        homepageSessionDurations.append(item);
    }
}

function formatDurationRange(startSeconds: number, endSeconds: number | null): string {
    if (endSeconds == null) return `${startSeconds / 60}+ min`;
    if (endSeconds <= 60) return `${startSeconds}–${endSeconds} sec`;
    return `${startSeconds / 60}–${endSeconds / 60} min`;
}

function renderRanking(list: HTMLOListElement, values: RankedValue[]): void {
    list.replaceChildren();
    if (values.length === 0) {
        const item = document.createElement('li');
        item.textContent = adminStrings.telemetryNoData;
        list.append(item);
        return;
    }

    for (const value of values) {
        const item = document.createElement('li');
        const row = document.createElement('span');
        const label = document.createElement('span');
        const count = document.createElement('strong');
        label.textContent = value.value;
        count.textContent = numberFormatter.format(value.count);
        row.append(label, count);
        item.append(row);
        list.append(item);
    }
}

function renderSnapshot(snapshot: TelemetrySnapshot): void {
    connectedBrowsers.textContent = numberFormatter.format(snapshot.connectedBrowsers);
    pageViews.textContent = numberFormatter.format(snapshot.pageViews);
    uniqueVisitors.textContent = numberFormatter.format(snapshot.uniqueVisitors);
    averageHomepageSession.textContent = formatDuration(snapshot.averageHomepageSessionMinutes);
    const periodLabel = adminStrings.telemetryPeriods[snapshot.range];
    pageViewsPeriod.textContent = periodLabel;
    uniqueVisitorsPeriod.textContent = periodLabel;
    renderRanking(topPages, snapshot.topPages);
    renderRanking(topReferrers, snapshot.topReferrers);
    renderDurationHistogram(snapshot.homepageSessionDurations);
    renderActiveSessions(snapshot.activeSessions, snapshot.generatedUtc);
    const updatedTime = new Date(snapshot.generatedUtc).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'UTC'
    });
    trafficFreshness.textContent = adminStrings.telemetryUpdated(updatedTime);
}

async function loadTelemetry(): Promise<void> {
    if (!authenticated || document.hidden) return;
    const requestedRange = selectedRange;
    const response = await adminSession.request(`/api/admin/telemetry?range=${requestedRange}`);
    if (!response.ok) throw new Error(adminStrings.telemetryUnavailable);
    const snapshot = await response.json() as TelemetrySnapshot;
    if (requestedRange === selectedRange) renderSnapshot(snapshot);
}

async function loadExcludedBrowsers(): Promise<void> {
    if (!authenticated) return;
    const response = await adminSession.request('/api/admin/telemetry/excluded-browsers');
    if (!response.ok) throw new Error(adminStrings.telemetryUnavailable);
    const excluded = await response.json() as ExcludedBrowser[];
    renderExcludedBrowsers(excluded);
}

function renderExcludedBrowsers(excluded: ExcludedBrowser[]): void {
    excludedBrowserIds = new Set(excluded.map(item => item.browserId));
    excludedBrowsersList.replaceChildren();
    if (excluded.length === 0) {
        const item = document.createElement('li');
        item.textContent = adminStrings.excludedBrowsersEmpty;
        excludedBrowsersList.append(item);
    } else {
        for (const excludedBrowser of excluded) {
            const item = document.createElement('li');
            const label = document.createElement('span');
            const excludedDate = document.createElement('time');
            const includeButton = document.createElement('button');
            label.textContent = excludedBrowser.label ?? excludedBrowser.browserId;
            excludedDate.dateTime = excludedBrowser.excludedUtc;
            excludedDate.textContent = adminStrings.excludedOn(new Date(excludedBrowser.excludedUtc).toLocaleDateString('en-US'));
            includeButton.type = 'button';
            includeButton.className = 'secondary';
            includeButton.textContent = adminStrings.includeAgain;
            includeButton.addEventListener('click', () => void includeBrowserAgain(excludedBrowser.browserId));
            item.append(label, excludedDate, includeButton);
            excludedBrowsersList.append(item);
        }
    }
    updateExcludeButton();
}

function updateExcludeButton(): void {
    excludeMyVisitsButton.textContent = excludedBrowserIds.has(browserId)
        ? adminStrings.visitsHidden
        : adminStrings.excludeMyVisits;
}

async function includeBrowserAgain(targetBrowserId: string): Promise<void> {
    try {
        const response = await adminSession.request(
            `/api/admin/telemetry/excluded-browsers/${encodeURIComponent(targetBrowserId)}`,
            { method: 'DELETE' });
        if (!response.ok) throw new Error(await readAdminError(response, adminStrings.requestFailed));
        await refreshExcludedBrowsers();
    } catch (error) {
        setStatus(error instanceof Error ? error.message : adminStrings.requestFailed, 'error');
    }
}

async function refreshExcludedBrowsers(): Promise<void> {
    await loadExcludedBrowsers();
    await loadTelemetry().catch(() => setStatus(adminStrings.telemetryUnavailable, 'error'));
}

async function excludeBrowser(targetBrowserId: string, label: string | null): Promise<void> {
    const response = await adminSession.request('/api/admin/telemetry/excluded-browsers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ browserId: targetBrowserId, label })
    });
    if (!response.ok) throw new Error(await readAdminError(response, adminStrings.requestFailed));
    await refreshExcludedBrowsers();
}

function openExcludeDialog(targetBrowserId: string): void {
    pendingExcludeBrowserId = targetBrowserId;
    excludeVisitsTitle.textContent = targetBrowserId === browserId
        ? adminStrings.excludeDialogTitle
        : adminStrings.excludeSessionBrowser;
    excludeVisitsLabel.value = '';
    excludeVisitsDialog.showModal();
}

function startDashboardConnection(): void {
    if (!authenticated || dashboardConnection.state !== HubConnectionState.Disconnected) return;
    void dashboardConnection.start().catch(() => {
        if (!authenticated || connectionRetryTimeoutId != null) return;
        connectionRetryTimeoutId = window.setTimeout(() => {
            connectionRetryTimeoutId = null;
            startDashboardConnection();
        }, 10_000);
    });
}

function stopDashboardConnection(): void {
    if (connectionRetryTimeoutId != null) {
        window.clearTimeout(connectionRetryTimeoutId);
        connectionRetryTimeoutId = null;
    }
    if (dashboardConnection.state !== HubConnectionState.Disconnected) void dashboardConnection.stop();
}

dashboardConnection.on('ConnectedBrowsersChanged', (count: unknown) => {
    if (authenticated && typeof count === 'number') connectedBrowsers.textContent = numberFormatter.format(count);
});
dashboardConnection.on('SessionsChanged', () => {
    void loadTelemetry().catch(() => setStatus(adminStrings.telemetryUnavailable, 'error'));
});
dashboardConnection.onreconnected(() => void loadTelemetry().catch(() => setStatus(adminStrings.telemetryUnavailable, 'error')));

for (const button of rangeButtons) {
    button.addEventListener('click', () => {
        if (!isTelemetryRange(button.dataset.range) || selectedRange === button.dataset.range) return;
        selectedRange = button.dataset.range;
        for (const candidate of rangeButtons) candidate.setAttribute('aria-pressed', String(candidate === button));
        setStatus(adminStrings.telemetryLoading);
        void loadTelemetry()
            .then(() => setStatus(''))
            .catch(() => setStatus(adminStrings.telemetryUnavailable, 'error'));
    });
}

excludeMyVisitsButton.addEventListener('click', () => {
    if (excludedBrowserIds.has(browserId)) {
        void includeBrowserAgain(browserId);
        return;
    }
    openExcludeDialog(browserId);
});

excludeVisitsCancel.addEventListener('click', () => excludeVisitsDialog.close());

excludeVisitsConfirm.addEventListener('click', () => void (async () => {
    if (pendingExcludeBrowserId == null) return;
    const label = excludeVisitsLabel.value.trim();
    try {
        await excludeBrowser(pendingExcludeBrowserId, label.length === 0 ? null : label);
        excludeVisitsDialog.close();
    } catch (error) {
        setStatus(error instanceof Error ? error.message : adminStrings.requestFailed, 'error');
    }
})());

const ordersToShipBadge = requiredElement<HTMLElement>('orders-to-ship');

/** Shows how many print orders are waiting on the dashboard's orders card; hidden when the shop is off. */
async function loadOrdersSummary(): Promise<void> {
    const response = await adminSession.request('/api/admin/orders/summary');
    if (!response.ok) {
        ordersToShipBadge.hidden = true;
        return;
    }
    const summary = await response.json() as { enabled: boolean; toShip: number; dueToday: number; overdue: number };
    ordersToShipBadge.hidden = !summary.enabled;
    ordersToShipBadge.textContent = adminStrings.ordersToShip(summary.toShip, summary.overdue, summary.dueToday);
    ordersToShipBadge.classList.toggle('attention', summary.overdue > 0 || summary.dueToday > 0);
}

const insightsTeaser = requiredElement<HTMLElement>('insights-teaser');

/** Names the 30-day "Strongest pull" leader on the Insights card; stays hidden if the report cannot be read. */
async function loadInsightsTeaser(): Promise<void> {
    const response = await adminSession.request('/api/admin/insights?range=30days');
    if (!response.ok) {
        insightsTeaser.hidden = true;
        return;
    }
    const report = await response.json() as InsightsReport;
    const leader = pickTeaserPhoto(report.photos ?? []);
    insightsTeaser.textContent = leader?.title == null ? adminStrings.insightsCollecting : adminStrings.insightsTeaser(leader.title);
    insightsTeaser.hidden = false;
}

const adminSession = createAdminSession({
    signedOutMessage: adminStrings.signedOut,
    loginFailedMessage: adminStrings.loginFailed,
    requestFailedMessage: adminStrings.requestFailed,
    setStatus,
    onAuthenticated: async () => {
        authenticated = true;
        setStatus(adminStrings.telemetryLoading);
        startDashboardConnection();
        void loadOrdersSummary().catch(() => {
            ordersToShipBadge.hidden = true;
        });
        void loadInsightsTeaser().catch(() => {
            insightsTeaser.hidden = true;
        });
        await loadTelemetry();
        setStatus('');
        await loadExcludedBrowsers().catch(() => setStatus(adminStrings.telemetryUnavailable, 'error'));
    },
    onSignedOut: () => {
        authenticated = false;
        stopDashboardConnection();
        clearTelemetry();
        excludedBrowserIds = new Set();
        excludedBrowsersList.replaceChildren();
        updateExcludeButton();
        ordersToShipBadge.hidden = true;
        insightsTeaser.hidden = true;
    }
});

window.setInterval(() => {
    void loadTelemetry().catch(() => setStatus(adminStrings.telemetryUnavailable, 'error'));
}, 30_000);
document.addEventListener('visibilitychange', () => {
    if (!document.hidden && authenticated) {
        startDashboardConnection();
        void loadTelemetry().catch(() => setStatus(adminStrings.telemetryUnavailable, 'error'));
    }
});

void adminSession.initialize()
    .catch(error => setStatus(error instanceof Error ? error.message : adminStrings.requestFailed, 'error'));